import {
  Component,
  computed,
  inject,
  signal,
  input,
  output,
  effect
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { GestionTarea } from 'src/app/domain/model/gestion-tarea/gestion-tarea';
import { GestionTareaRepository } from 'src/app/domain/service/gestion-tarea/gestion-tarea-repository';
import { historialGestionesPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * HistorialGestionesDialogComponent
 *
 * Diálogo que muestra el historial de gestiones realizadas para una orden de servicio.
 * Replica la funcionalidad del legacy CallsHistoryInterfaceImpl.java
 *
 * Muestra una tabla con las gestiones ordenadas por fecha descendente (más recientes primero).
 *
 * @example
 * ```html
 * <app-historial-gestiones-dialog
 *   [visible]="showHistorial()"
 *   [nmconsecutivoDetalle]="ordenSeleccionada()?.nmconsecutivo"
 *   [nombreBeneficiario]="detalle()?.nombreBeneficiario"
 *   [tarea]="detalle()?.tareaCliente"
 *   (onClose)="closeHistorial()">
 * </app-historial-gestiones-dialog>
 * ```
 *
 * Legacy Reference: CallsHistoryInterfaceImpl.java
 *
 * @author GIROP Migration
 * @since 1.0
 */
@Component({
  selector: 'app-historial-gestiones-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TableModule,
    ProgressBarModule,
    MessageModule,
    TagModule,
    TooltipModule,
    DividerModule
  ],
  providers: [DatePipe],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="'Historial de Gestiones'"
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '68rem' }"
      [contentStyle]="{ 'max-height': '75vh', overflow: 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onDialogVisibleChange($event)"
    >
      <!-- Información del Beneficiario y Tarea -->
      @if (nombreBeneficiario() || tarea()) {
        <div class="p-3 rounded-lg mb-4 flex flex-wrap gap-4">
          @if (nombreBeneficiario()) {
            <div class="flex items-center gap-2">
              <i class="pi pi-user text-blue-500"></i>
              <span class="text-gray-600 text-sm">Beneficiario:</span>
              <span class="font-medium text-sm">{{
                nombreBeneficiario()
              }}</span>
            </div>
          }
          @if (tarea()) {
            <div class="flex items-center gap-2">
              <i class="pi pi-briefcase text-green-500"></i>
              <span class="text-gray-600 text-sm">Tarea:</span>
              <span class="font-medium text-sm">{{ tarea() }}</span>
            </div>
          }
        </div>
      }

      <!-- Cargando -->
      @if (cargando()) {
        <div class="flex flex-col items-center py-8">
          <p-progressBar
            mode="indeterminate"
            [style]="{ width: '200px' }"
          ></p-progressBar>
          <p class="text-gray-500 mt-4">Cargando historial de gestiones...</p>
        </div>
      }

      <!-- Error -->
      @if (error()) {
        <p-message
          severity="error"
          [text]="error()!"
          styleClass="w-full mb-4"
        ></p-message>
      }

      <!-- Sin gestiones -->
      @if (!cargando() && !error() && gestiones().length === 0) {
        <div class="flex flex-col items-center py-8">
          <i class="pi pi-phone text-4xl text-gray-300 mb-4"></i>
          <p class="text-gray-500">
            No hay gestiones registradas para esta orden.
          </p>
        </div>
      }

      <!-- Tabla de gestiones -->
      @if (!cargando() && !error() && gestiones().length > 0) {
        <div class="md:hidden space-y-3 p-3">
          @for (
            gestion of gestiones();
            track gestion.nmconsecutivoLlamada ?? $index
          ) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Gestión
                  </div>
                  <div class="font-semibold text-gray-800 dark:text-gray-100">
                    #{{ gestion.nmconsecutivoLlamada ?? '-' }}
                  </div>
                </div>
                <button
                  pButton
                  type="button"
                  icon="pi pi-eye"
                  class="p-button-text p-button-sm p-button-rounded min-w-[44px] min-h-[44px]"
                  pTooltip="Ver observaciones"
                  [disabled]="!gestion.dsobservaciones"
                  (click)="verObservaciones(gestion)"
                ></button>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Fecha:</span>
                  @if (gestion.feLlamada) {
                    <span class="ml-1 text-gray-800 dark:text-gray-200">{{
                      formatearFecha(gestion.feLlamada)
                    }}</span>
                  } @else {
                    <span class="ml-1 text-gray-400">-</span>
                  }
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Responde:</span
                  >
                  <span class="ml-1 text-gray-800 dark:text-gray-200">{{
                    gestion.dsRespondeLlamada || '-'
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Tipo respuesta:</span
                  >
                  @if (gestion.dsRespuestaLlamada) {
                    <p-tag
                      [value]="gestion.dsRespuestaLlamada"
                      [severity]="
                        getSeverityRespuesta(gestion.dsRespuestaLlamada)
                      "
                    ></p-tag>
                  } @else {
                    <span class="ml-1 text-gray-400">Sin respuesta</span>
                  }
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Nueva gestión:</span
                  >
                  @if (gestion.feNuevaLlamada) {
                    <span class="ml-1 text-blue-600">{{
                      formatearFecha(gestion.feNuevaLlamada)
                    }}</span>
                  } @else {
                    <span class="ml-1 text-gray-400">-</span>
                  }
                </div>
              </div>
            </div>
          }
          <div class="text-sm text-gray-600 px-1">
            Total: {{ gestiones().length }} gestión(es)
          </div>
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="gestiones()"
            [paginator]="
              tableConfig().pagination.enabled &&
              gestiones().length > tableConfig().pagination.pageSize
            "
            [rows]="tableConfig().pagination.pageSize"
            [rowsPerPageOptions]="
              tableConfig().pagination.pageSizeOptions || [5, 10, 25]
            "
            [showCurrentPageReport]="tableConfig().pagination.enabled"
            [currentPageReportTemplate]="
              tableConfig().pagination.currentPageReportTemplate ||
              'Registros del {first} hasta el {last} de {totalRecords}'
            "
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '450px'"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || '72rem'
            }"
            [styleClass]="tableStyleClass()"
            [paginatorDropdownAppendTo]="'body'"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn style="width: 100px" class="text-center">
                  <span class="grid-header-text" title="Número">Número</span>
                </th>
                <th pResizableColumn style="width: 150px">
                  <span class="grid-header-text" title="Fecha">Fecha</span>
                </th>
                <th pResizableColumn style="width: 160px">
                  <span class="grid-header-text" title="Responde"
                    >Responde</span
                  >
                </th>
                <th pResizableColumn style="width: 200px">
                  <span class="grid-header-text" title="Tipo Respuesta"
                    >Tipo Respuesta</span
                  >
                </th>
                <th pResizableColumn style="width: 150px">
                  <span class="grid-header-text" title="Nueva Gestión"
                    >Nueva Gestión</span
                  >
                </th>
                <th pResizableColumn style="width: 100px" class="text-center">
                  <span class="grid-header-text" title="Acciones"
                    >Acciones</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-gestion>
              <tr>
                <td class="text-center font-medium">
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(gestion.nmconsecutivoLlamada)"
                    >{{ textoTabla(gestion.nmconsecutivoLlamada) }}</span
                  >
                </td>
                <td>
                  @if (gestion.feLlamada) {
                    <span
                      class="grid-cell-text"
                      [pTooltip]="formatearFechaCompleta(gestion.feLlamada)"
                      [attr.title]="formatearFechaCompleta(gestion.feLlamada)"
                    >
                      {{ textoTabla(formatearFecha(gestion.feLlamada)) }}
                    </span>
                  } @else {
                    <span class="grid-cell-text text-gray-400" title="-"
                      >-</span
                    >
                  }
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(gestion.dsRespondeLlamada)"
                    >{{ textoTabla(gestion.dsRespondeLlamada) }}</span
                  >
                </td>
                <td>
                  @if (gestion.dsRespuestaLlamada) {
                    <p-tag
                      [value]="gestion.dsRespuestaLlamada"
                      [severity]="
                        getSeverityRespuesta(gestion.dsRespuestaLlamada)
                      "
                    >
                    </p-tag>
                  } @else {
                    <span class="text-gray-400">Sin respuesta</span>
                  }
                </td>
                <td>
                  @if (gestion.feNuevaLlamada) {
                    <span
                      class="grid-cell-text text-blue-600"
                      [attr.title]="
                        formatearFechaCompleta(gestion.feNuevaLlamada)
                      "
                    >
                      {{ textoTabla(formatearFecha(gestion.feNuevaLlamada)) }}
                    </span>
                  } @else {
                    <span class="grid-cell-text text-gray-400" title="-"
                      >-</span
                    >
                  }
                </td>
                <td class="text-center">
                  <button
                    pButton
                    type="button"
                    icon="pi pi-eye"
                    class="p-button-text p-button-sm p-button-rounded min-w-[44px] min-h-[44px]"
                    pTooltip="Ver observaciones"
                    tooltipPosition="left"
                    [disabled]="!gestion.dsobservaciones"
                    (click)="verObservaciones(gestion)"
                  ></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="summary">
              <div
                class="flex justify-between items-center text-sm text-gray-600"
              >
                <span>Total: {{ gestiones().length }} gestión(es)</span>
              </div>
            </ng-template>
          </p-table>
        </div>
      }

      <!-- Footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary min-h-[44px]"
            (click)="cerrar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Modal de Observaciones -->
    <p-dialog
      header="Observaciones de la Gestión"
      [visible]="mostrarObservaciones()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '38rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      (visibleChange)="cerrarObservaciones()"
    >
      @if (gestionSeleccionada()) {
        <div class="mb-3 text-sm text-gray-500">
          <span class="font-medium"
            >Gestión #{{ gestionSeleccionada()!.nmconsecutivoLlamada }}</span
          >
          @if (gestionSeleccionada()!.feLlamada) {
            <span>
              - {{ formatearFecha(gestionSeleccionada()!.feLlamada!) }}</span
            >
          }
        </div>
        <p-divider></p-divider>
        <div class="p-4 rounded-lg whitespace-pre-wrap text-sm leading-relaxed">
          {{ gestionSeleccionada()!.dsobservaciones || 'Sin observaciones' }}
        </div>
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary min-h-[44px]"
            (click)="cerrarObservaciones()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        font-weight: 600;
      }
      :host ::ng-deep .p-datatable-scrollable .p-datatable-thead {
        position: sticky;
        top: 0;
        z-index: 1;
      }
    `
  ]
})
export class HistorialGestionesDialogComponent {
  // === Inputs/Outputs ===

  /** Indica si el diálogo está visible */
  visible = input<boolean>(false);

  /** Consecutivo del detalle de orden de servicio */
  nmconsecutivoDetalle = input<number | null>(null);

  /** Nombre del beneficiario/cliente */
  nombreBeneficiario = input<string | null>(null);

  /** Nombre de la tarea */
  tarea = input<string | null>(null);

  /** Evento emitido al cerrar el diálogo */
  onClose = output<void>();

  // === Dependencias ===

  private readonly repository = inject(GestionTareaRepository);
  private readonly datePipe = inject(DatePipe);

  // === Estado ===

  readonly cargando = signal(false);
  readonly error = signal<string | null>(null);
  readonly gestiones = signal<GestionTarea[]>([]);

  // Modal de observaciones
  readonly mostrarObservaciones = signal(false);
  readonly gestionSeleccionada = signal<GestionTarea | null>(null);

  readonly tableConfig = computed(() =>
    historialGestionesPreset.createConfig({
      state: {
        loading: this.cargando(),
        emptyMessage: 'No hay gestiones registradas para esta orden.'
      },
      a11y: {
        tableAriaLabel: 'Tabla de historial de gestiones',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // === Constructor ===

  constructor() {
    // Efecto para cargar datos cuando se abre el diálogo
    effect(() => {
      const isVisible = this.visible();
      const nmconsec = this.nmconsecutivoDetalle();

      if (isVisible && nmconsec) {
        this.cargarHistorial(nmconsec);
      } else if (!isVisible) {
        // Limpiar al cerrar
        this.gestiones.set([]);
        this.error.set(null);
        this.mostrarObservaciones.set(false);
        this.gestionSeleccionada.set(null);
      }
    });
  }

  // === Métodos ===

  /**
   * Carga el historial de gestiones desde el backend.
   */
  private async cargarHistorial(nmconsecutivo: number): Promise<void> {
    this.cargando.set(true);
    this.error.set(null);

    try {
      const gestiones =
        await this.repository.consultarHistorialGestiones(nmconsecutivo);
      this.gestiones.set(gestiones);
    } catch (err) {
      console.error('[HistorialGestiones] Error cargando historial:', err);
      this.error.set(
        'Error al cargar el historial de gestiones. Intente nuevamente.'
      );
    } finally {
      this.cargando.set(false);
    }
  }

  /**
   * Abre el modal de observaciones para una gestión.
   */
  verObservaciones(gestion: GestionTarea): void {
    this.gestionSeleccionada.set(gestion);
    this.mostrarObservaciones.set(true);
  }

  /**
   * Cierra el modal de observaciones.
   */
  cerrarObservaciones(): void {
    this.mostrarObservaciones.set(false);
    this.gestionSeleccionada.set(null);
  }

  /**
   * Maneja cambios en la visibilidad del diálogo.
   */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.onClose.emit();
    }
  }

  /**
   * Cierra el diálogo.
   */
  cerrar(): void {
    this.onClose.emit();
  }

  /**
   * Formatea una fecha ISO a formato corto dd/MM/yyyy HH:mm
   */
  formatearFecha(fechaIso: string): string {
    if (!fechaIso) return '-';
    try {
      const fecha = new Date(fechaIso);
      return this.datePipe.transform(fecha, 'dd/MM/yyyy HH:mm') || fechaIso;
    } catch {
      return fechaIso;
    }
  }

  /**
   * Formatea una fecha ISO a formato completo para tooltip.
   */
  formatearFechaCompleta(fechaIso: string): string {
    if (!fechaIso) return '';
    try {
      const fecha = new Date(fechaIso);
      return (
        this.datePipe.transform(fecha, 'EEEE, dd MMMM yyyy HH:mm:ss') ||
        fechaIso
      );
    } catch {
      return fechaIso;
    }
  }

  /**
   * Trunca un texto a una longitud máxima.
   */
  truncarTexto(texto: string, maxLength: number): string {
    if (!texto || texto.length <= maxLength) return texto;
    return texto.substring(0, maxLength) + '...';
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * Retorna el severity del tag según el tipo de respuesta.
   * Legacy: Categorización visual de respuestas.
   */
  getSeverityRespuesta(
    respuesta: string
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (!respuesta) return 'secondary';

    const respuestaLower = respuesta.toLowerCase();

    // Respuestas positivas
    if (
      respuestaLower.includes('confirm') ||
      respuestaLower.includes('acepta') ||
      respuestaLower.includes('exitosa')
    ) {
      return 'success';
    }

    // Respuestas de cancelación/rechazo
    if (
      respuestaLower.includes('cancel') ||
      respuestaLower.includes('rechaz') ||
      respuestaLower.includes('no acepta')
    ) {
      return 'danger';
    }

    // Respuestas de espera/pendiente
    if (
      respuestaLower.includes('pend') ||
      respuestaLower.includes('esper') ||
      respuestaLower.includes('no contesta')
    ) {
      return 'warn';
    }

    return 'info';
  }
}
